import { Partners, ProgramCodes, PromoCodes } from '@mesa-labs/mesa-api/dist/types';
import { useNavigate } from 'react-router-dom';

import { useDispatch } from '../redux/hooks';
import { signIn } from '../redux/slices/auth';
import { ExtraDemoCodes, resetDemoMode, updateDemoModeCode, updateDemoModeLogoSrc, updateIsEnabled, updateOverrideFinished, type DemoModeCodes } from '../redux/slices/demoMode';
import { updatePartnerId, updateProgramCode, updatePromoCode } from '../redux/slices/onboarding';
import { updateVendorField } from '../redux/slices/vendor';
import { updateVendorAccountField } from '../redux/slices/vendorAccount';
import { updateSelectedPartner } from '../redux/slices/selectedPartner';
import { isRejected } from '@reduxjs/toolkit';

// Re-export from shared location (aliased for backward compatibility)
export { DEMO_VENDORS_REQUIRING_PLAID_RECONNECTION as OPTION_DEMO_VENDOR_REQUIRING_PLAID_RECONNECTION } from '../../data/demoVendorSelectors';

export const OPTION_TO_COGNITO_USERNAME: Partial<Record<ProgramCodes | ExtraDemoCodes, string>> = {
  [ExtraDemoCodes.NET30_AND_POA]: '9f902e61-d17b-4faa-be94-5386ce331817',
  [ExtraDemoCodes.NET15_AND_POA_BEELINE]: '9f902e61-d17b-4faa-be94-5386ce331817',
  [ProgramCodes.NET30]: '9f902e61-d17b-4faa-be94-5386ce331817',
  [ProgramCodes.POA]: '84aad4eb-407e-4bec-ad64-12f50dfa045f',
  [ProgramCodes.OD]: '66a3d769-152d-4af9-b67b-730cb97b95c4',
  [ExtraDemoCodes.DIRECT_DEBIT_NET_30]: '739ea766-277a-44ca-8595-b754b41d7a74',
  [ExtraDemoCodes.DIRECT_DEBIT_POA]: '1aa3cf29-e287-457c-a5be-a74157f9c10d',
  [ExtraDemoCodes.DIRECT_DEBIT_BUILT_POA]: '1aa3cf29-e287-457c-a5be-a74157f9c10d',
  [ExtraDemoCodes.DIRECT_DEBIT_TUNGSTEN_AUTOMATION_ON_DEMAND]: '09a949be-5031-70dc-28ad-eef19f94a53e',
  [ExtraDemoCodes.DIRECT_DEBIT_SERVICE_TITAN_POA]: '1aa3cf29-e287-457c-a5be-a74157f9c10d',
  [ExtraDemoCodes.DIRECT_DEBIT_CORRIGO_POA]: '6aea43e9-627f-4fbd-a17d-46703f689c6c',
  [ExtraDemoCodes.DIRECT_DEBIT_ITRADE_POA]: '89ba6f11-c246-4ae1-ae02-a3a6b773745d',
  [ExtraDemoCodes.DIRECT_DEBIT_DIRECT_COMMERCE_POA]: '1aa3cf29-e287-457c-a5be-a74157f9c10d',
  [ExtraDemoCodes.DIRECT_DEBIT_WORKDAY_VNDLY_POA]: '1aa3cf29-e287-457c-a5be-a74157f9c10d',
  [ExtraDemoCodes.TAPFIN_NET30]: '9f902e61-d17b-4faa-be94-5386ce331817',
  [ExtraDemoCodes.KELLY_OCG_NET30]: '9f902e61-d17b-4faa-be94-5386ce331817',
  [ExtraDemoCodes.AGS_NET30]: '9f902e61-d17b-4faa-be94-5386ce331817',
  [ExtraDemoCodes.RANDSTAD_NET30]: '9f902e61-d17b-4faa-be94-5386ce331817',
  [ExtraDemoCodes.DIRECT_DEBIT_GHX_NET30]: '739ea766-277a-44ca-8595-b754b41d7a74',
  [ExtraDemoCodes.DIRECT_DEBIT_OD]: 'c9a929be-f071-7021-f1ff-cbe41c73b0d0',
};

type DemoModeConfig = {
  demoCode: DemoModeCodes;
  programCode: ProgramCodes;
  promoCode?: PromoCodes;
  partnerId: Partners;
  selectedPartner?: Partners;
  logoSrc?: string;
  businessName?: string;
};

const DEMO_MODE_CONFIGS: Partial<Record<ProgramCodes | ExtraDemoCodes, DemoModeConfig>> = {
  [ProgramCodes.OD]: {
    demoCode: ProgramCodes.OD,
    programCode: ProgramCodes.OD,
    partnerId: Partners.JLL,
  },
  [ProgramCodes.POA]: {
    demoCode: ProgramCodes.POA,
    programCode: ProgramCodes.POA,
    partnerId: Partners.JLL,
  },
  [ProgramCodes.NET30]: {
    demoCode: ProgramCodes.NET30,
    programCode: ProgramCodes.NET30,
    partnerId: Partners.JLL,
  },
  [ExtraDemoCodes.NET15_AND_POA_BEELINE]: {
    demoCode: ExtraDemoCodes.NET15_AND_POA_BEELINE,
    programCode: ProgramCodes.NET15,
    partnerId: Partners.NO_PARTNER,
    logoSrc: 'mesa-beeline.svg',
  },
  [ExtraDemoCodes.DIRECT_DEBIT_NET_30]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_NET_30,
    programCode: ProgramCodes.NET30,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.ITRADE,
  },
  [ExtraDemoCodes.DIRECT_DEBIT_POA]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_POA,
    programCode: ProgramCodes.POA,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.ITRADE,
    selectedPartner: Partners.GHX,
  },
  [ExtraDemoCodes.DIRECT_DEBIT_BUILT_POA]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_BUILT_POA,
    programCode: ProgramCodes.POA,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.NO_PARTNER,
    logoSrc: 'mesa-built.svg',
  },
  [ExtraDemoCodes.DIRECT_DEBIT_SERVICE_TITAN_POA]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_SERVICE_TITAN_POA,
    programCode: ProgramCodes.POA,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.NO_PARTNER,
    logoSrc: 'mesa-service-titan.svg',
  },
  [ExtraDemoCodes.DIRECT_DEBIT_CORRIGO_POA]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_CORRIGO_POA,
    programCode: ProgramCodes.POA,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.CORRIGO,
  },
  [ExtraDemoCodes.DIRECT_DEBIT_DIRECT_COMMERCE_POA]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_DIRECT_COMMERCE_POA,
    programCode: ProgramCodes.POA,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.NO_PARTNER,
    logoSrc: 'mesa-direct-commerce.webp',
  },
  [ExtraDemoCodes.DIRECT_DEBIT_ITRADE_POA]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_ITRADE_POA,
    programCode: ProgramCodes.POA,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.ITRADE,
    selectedPartner: Partners.ITRADE,
  },
  [ExtraDemoCodes.DIRECT_DEBIT_WORKDAY_VNDLY_POA]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_WORKDAY_VNDLY_POA,
    programCode: ProgramCodes.POA,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.ITRADE,
    logoSrc: 'mesa-workday-vndly.svg',
    businessName: 'Boulder Staffing',
  },
  [ExtraDemoCodes.TAPFIN_NET30]: {
    demoCode: ExtraDemoCodes.TAPFIN_NET30,
    programCode: ProgramCodes.NET30,
    partnerId: Partners.JLL,
    logoSrc: 'mesa-tapfin.svg',
    businessName: 'Boulder Staffing',
  },
  [ExtraDemoCodes.RANDSTAD_NET30]: {
    demoCode: ExtraDemoCodes.RANDSTAD_NET30,
    programCode: ProgramCodes.NET30,
    partnerId: Partners.JLL,
    logoSrc: 'mesa-randstad.svg',
    businessName: 'Boulder Staffing',
  },
  [ExtraDemoCodes.KELLY_OCG_NET30]: {
    demoCode: ExtraDemoCodes.KELLY_OCG_NET30,
    programCode: ProgramCodes.NET30,
    partnerId: Partners.JLL,
    logoSrc: 'mesa-kellyocg.svg',
    businessName: 'Boulder Staffing',
  },
  [ExtraDemoCodes.AGS_NET30]: {
    demoCode: ExtraDemoCodes.AGS_NET30,
    programCode: ProgramCodes.NET30,
    partnerId: Partners.JLL,
    logoSrc: 'mesa-ags.svg',
    businessName: 'Boulder Staffing',
  },
  [ExtraDemoCodes.DIRECT_DEBIT_GHX_NET30]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_GHX_NET30,
    programCode: ProgramCodes.NET30,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.GHX,
    selectedPartner: Partners.GHX,
  },
  [ExtraDemoCodes.DIRECT_DEBIT_TUNGSTEN_AUTOMATION_ON_DEMAND]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_TUNGSTEN_AUTOMATION_ON_DEMAND,
    programCode: ProgramCodes.OD,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.TUNGSTEN,
    selectedPartner: Partners.TUNGSTEN,
    logoSrc: 'mesa-tungsten-automation.svg',
  },
  [ExtraDemoCodes.DIRECT_DEBIT_OD]: {
    demoCode: ExtraDemoCodes.DIRECT_DEBIT_OD,
    programCode: ProgramCodes.OD,
    promoCode: PromoCodes.DIRECT_DEBIT,
    partnerId: Partners.CORRIGO,
  },
};

const DEFAULT_CONFIG: DemoModeConfig = {
  demoCode: ExtraDemoCodes.NET30_AND_POA,
  programCode: ProgramCodes.NET30,
  partnerId: Partners.JLL,
};

function useStartDemoMode(): ({ option, password, navigateTo }: { option: keyof typeof OPTION_TO_COGNITO_USERNAME, password: string, navigateTo: 'onboarding' | 'dashboard' }) => void {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return async ({ option, password, navigateTo }: { option: string, password: string, navigateTo: 'onboarding' | 'dashboard' }) => {
    dispatch(resetDemoMode());
    dispatch(updateIsEnabled(true));
    dispatch(updateVendorField({ field: 'email', value: 'test@boulderhvac.com' }));
    dispatch(updateVendorField({ field: 'name', value: 'Boulder HVAC & Plumbing' }));
    dispatch(updateVendorField({ field: 'address', value: '780 16th St.' }));
    dispatch(updateVendorField({ field: 'city', value: 'Boulder' }));
    dispatch(updateVendorField({ field: 'firstName', value: 'Ben' }));
    dispatch(updateVendorField({ field: 'lastName', value: 'Apel' }));
    dispatch(updateVendorField({ field: 'phoneNumber', value: '(123) 456-7890' }));
    dispatch(updateVendorField({ field: 'state', value: 'CO' }));
    dispatch(updateVendorField({ field: 'title', value: 'CEO' }));
    dispatch(updateVendorField({ field: 'zip', value: '80306' }));
    dispatch(updateVendorAccountField({ field: 'name', value: 'Mesa Checking Account' }));
    dispatch(updateVendorAccountField({ field: 'accountNumber', value: '50952189' }));
    dispatch(updateVendorAccountField({ field: 'confirmAccountNumber', value: '50952189' }));
    dispatch(updateVendorAccountField({ field: 'routingNumber', value: '631916385' }));

    const email = OPTION_TO_COGNITO_USERNAME[option as keyof typeof OPTION_TO_COGNITO_USERNAME];

    if (!email) {
      throw new Error('Invalid demo mode option');
    }

    // sign in with the id and password
    const initialResponse = await dispatch(signIn({
      email,
      password,
      totpCallback: () => Promise.resolve(''),
      smsCodeCallback: () => Promise.resolve(''),
      skipParentIdCheck: true,
    }));

    if (isRejected(initialResponse)) {
      throw new Error('Incorrect password, please try again.');
    } else {
      // the response contains the email, so re-sign in with the email
      await dispatch(signIn({
        email: initialResponse.payload.email,
        password,
        totpCallback: () => Promise.resolve(''),
        smsCodeCallback: () => Promise.resolve(''),
      }));
    }

    const config = DEMO_MODE_CONFIGS[option as ProgramCodes | ExtraDemoCodes] ?? DEFAULT_CONFIG;

    dispatch(updateDemoModeCode(config.demoCode));
    dispatch(updateProgramCode(config.programCode));
    dispatch(updatePromoCode(config.promoCode));
    dispatch(updatePartnerId(config.partnerId));
    dispatch(updateSelectedPartner(config.selectedPartner));
    dispatch(updateDemoModeLogoSrc(config.logoSrc));

    if (config.businessName) {
      dispatch(updateVendorField({ field: 'name', value: config.businessName }));
    }

    if (navigateTo === 'dashboard') {
      dispatch(updateOverrideFinished(true));
    }

    navigate(navigateTo === 'dashboard' ? '/' : '/onboarding/business-info');
  };
}

export default useStartDemoMode;
